// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import useConfigList from '@Hooks/useConfigList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import React, { ReactElement, useContext, useMemo } from 'react';
import { ObjectTypeV1 as ObjectType, FunctionOutput, OrgPermissionsEnum, PermissionsEnum } from 'thehive-sdk';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import client, { queryClient } from '@Client';
import { THLayout } from '@Molecules/THLayout';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { ICONS } from '@Constants/iconsApp';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { FormatDate, FormatMultipleDate } from '@Molecules/Format';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { computeListSelector } from '@Organisms/Functions/selector';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { useDrawer } from '@Hooks/useDrawer';
import { computeEventList } from '@Organisms/Functions/eventList';
import { FunctionListTitle } from '@Organisms/Functions/FunctionListTitle/FunctionListTitle';
import { FunctionManage } from '@Organisms/Functions/FunctionManage/FunctionManage';
import { StatusContext } from '@Context/StatusProvider';
import { UnavailableMessage } from '@Molecules/UnavailableMessage/UnavailableMessage';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { useCurrentUser } from '@Queries/userQueries';

import './FunctionsList.scss';

const FunctionsList = (): ReactElement => {
    const i = useLocalMessage();

    const { data: currentUser } = useCurrentUser();

    const {
        visible: visibleCreate,
        setVisible: setVisibleCreate,
        data: dataCreate,
        setData: setDataCreate,
    } = useDrawer<FunctionOutput>({});

    const config = useConfigList<FunctionOutput>({
        selector: computeListSelector(),
        controller: client.FunctionController,
        storageId: StorageFilterEnum.FUNCTION,
        objectType: ObjectType.FUNCTION,
    });

    const {
        countRefetch,
        dataSource = [],
        columnsRenderer,
        listRefetch,
        actionListRenderer,
        status = 'loading',
        table,
        filterManager,
        paginationManager,
        storagePageManager,
        storageViewManager,
    } = config;

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.FunctionController,
                onError: handleNotificationError,
                locale: i,
                onSuccess: (msg) => {
                    msg && openMessage('success', msg);
                    listRefetch();
                    countRefetch();
                },
            }),
        [countRefetch, i, listRefetch],
    );

    const { permissions = [] } = currentUser || {};
    const hasPermissions = useSplitPermissions(permissions, [
        PermissionsEnum.MANAGE_FUNCTION_CREATE,
        PermissionsEnum.MANAGE_FUNCTION_INVOKE,
    ]);

    const { licenseCapabilities } = useContext(StatusContext);

    const functionCapabilities = useMemo(() => !!licenseCapabilities?.functions || false, [licenseCapabilities]);

    const actionList = actionListRenderer((selectedItems, onSuccess) => {
        const item = selectedItems.find(Boolean);

        return {
            edit: {
                label: i('general.edit'),
                query: () => {
                    setVisibleCreate(true);
                    setDataCreate(item);
                },
                icon: <ICONS.EditIcon />,
                scope: 'single',
                disabled: !hasPermissions.createFunction,
            },
            delete: {
                query: (): Promise<void> =>
                    client.FunctionController.delete(item?._id || '')
                        .then(() => {
                            openMessage('success', i('function.delete.success'));
                            onSuccess(true);
                        })
                        .catch(handleNotificationError),
                label: i('general.delete'),
                message: i('function.delete.single'),
                icon: <ICONS.DeleteIcon />,
                confirm: true,
                scope: 'single',
                disabled: !hasPermissions.createFunction,
            },
        };
    });

    const columns = columnsRenderer([
        {
            title: [{ label: i('function.list.columns.name'), sortField: 'name' }],
            className: 'functions-list__name',
            render: (funct): ReactElement => (
                <FunctionListTitle
                    data={funct}
                    onTitleClick={
                        hasPermissions.createFunction
                            ? () => {
                                  setVisibleCreate(true);
                                  setDataCreate(funct);
                              }
                            : undefined
                    }
                />
            ),
        },
        {
            title: [{ label: i('function.manage.mode.label'), sortField: 'mode' }],
            render: ({ mode }, { custom }): ReactElement => (
                <span onClick={() => custom({ field: 'mode', operator: 'any', values: [mode] })}>{mode}</span>
            ),
        },
        {
            title: i('function.list.columns.by'),
            className: 'functions-list__by',
            render: ({ _createdBy }): ReactElement => <AssigneeInfo assignee={_createdBy} avatarOnly />,
        },
        {
            title: [{ label: i('function.list.columns.last-success'), sortField: 'lastSuccessDate' }],
            className: 'functions-list__dates',
            render: ({ lastSuccessDate }, { date: filterDate }): ReactElement =>
                lastSuccessDate ? (
                    <FormatDate date={lastSuccessDate} onClick={(date) => filterDate(date, 'lastSuccessDate')} />
                ) : (
                    <>{i('general.none')}</>
                ),
        },
        {
            title: [{ label: i('function.list.columns.last-error'), sortField: 'lastErrorDate' }],
            className: 'functions-list__dates',
            render: ({ lastErrorDate }, { date: filterDate }): ReactElement =>
                lastErrorDate ? (
                    <FormatDate date={lastErrorDate} onClick={(date) => filterDate(date, 'lastErrorDate')} />
                ) : (
                    <>{i('general.none')}</>
                ),
        },
        {
            title: [
                { label: i('function.list.columns.dates') },
                { label: 'C.', sortField: '_createdAt' },
                { label: 'U.', sortField: '_updatedAt' },
            ],
            className: 'functions-list__dates',
            render: ({ _createdAt = 0, _updatedAt = 0 }, { date: filterDate }): ReactElement => (
                <FormatMultipleDate
                    dates={[
                        {
                            label: 'C.',
                            value: _createdAt,
                            onClick: (): void => filterDate(_createdAt, '_createdAt'),
                        },
                        {
                            label: 'U.',
                            value: _updatedAt,
                            onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                        },
                    ]}
                />
            ),
        },
        {
            className: 'functions-list__actions',
            hidden: !hasPermissions.createFunction && !hasPermissions.invokeFunction,
            render: (func): ReactElement | null => (
                <ActionsList
                    actionList={actionList([func], (count) => {
                        queryClient.refetchQueries([computeListSelector().name]);
                        if (count) {
                            countRefetch();
                        }
                    })}
                    scope="single"
                    isIcon
                />
            ),
        },
    ]);

    const { data: describe } = useListAllFields();

    if (!functionCapabilities) {
        return <UnavailableMessage type="capability" />;
    }

    return (
        <>
            <THLayout>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {hasPermissions.createFunction && (
                                <AddButton
                                    label={i('function.manage.drawer.create')}
                                    onClick={() => setVisibleCreate(true)}
                                />
                            )}
                            {storagePageManager && storageViewManager ? (
                                <ManageViewList
                                    pageManager={storagePageManager}
                                    viewManager={storageViewManager}
                                    beforeCreate={(content: any): any => {
                                        filterManager.applyFilters();
                                        return { ...content, filters: filterManager.tempFilters };
                                    }}
                                />
                            ) : null}
                        </THList.Slot>
                        <THList.Slot name="NAVIGATION">
                            <ManageSwitch
                                label={i('common.filters')}
                                value={filterManager.editFilters}
                                onChange={(editFilters) => {
                                    filterManager.setEditFilters(editFilters);
                                }}
                                icon={<ICONS.FilterOutlined />}
                            />
                        </THList.Slot>

                        <THList.Slot name="CONTENT">
                            {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                                <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.FUNCTION]}>
                                    <Footer filterConfig={filterManager} />
                                </THFilter>
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={dataSource}
                        columns={columns}
                        rowKey="_id"
                        emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('function.none')} />}
                    />

                    <THList.Footer>
                        <THList.Slot name="NAVIGATION">
                            <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                            <PaginationSize paginationConfig={paginationManager} />
                        </THList.Slot>
                    </THList.Footer>
                </THList>
            </THLayout>

            {visibleCreate && (
                <FunctionManage
                    eventList={eventList}
                    defaultValues={dataCreate}
                    visible={visibleCreate}
                    onVisibleChange={(visible) => {
                        setVisibleCreate(visible);
                        if (!visible) {
                            setDataCreate(undefined);
                        }
                    }}
                    onFinish={({ definition, description, name, mode }) => {
                        if (dataCreate) {
                            return eventList.onEdit({ definition, description, name, mode }, dataCreate._id);
                        }
                        return eventList.onCreate({ definition, description, name, mode });
                    }}
                />
            )}
        </>
    );
};

const path = RouteBuilder.compute('administration.functions');
const pathUser = RouteBuilder.compute('organisation.functions');

const MenuTitle = (): ReactElement => {
    const i = useLocalMessage();

    return (
        <div>
            <ICONS.FunctionIcon /> {i('organisation.layout.nav-bar.functions')}
            <sup className="functions-list__sup">BETA</sup>
        </div>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'organisation.layout.nav-bar.functions',
        icon: <ICONS.FunctionIcon />,
        render: <MenuTitle />,
    },
    path,
    permissionNeeded: [[OrgPermissionsEnum.MANAGE_FUNCTION_CREATE], [OrgPermissionsEnum.MANAGE_FUNCTION_INVOKE]],
    content: <FunctionsList />,
    shared: true,
    space: ['admin'],
    breadcrumb: { label: 'breadcrumb.organisation.functions' },
};

export const RouteUser: RouteType = {
    ...Route,
    path: pathUser,
    space: ['user'],
};
